import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import SpiderChart from "../components/spider_chart";
import DropdownFilter from "../components/dropdown_filter";
import { fetchPosts } from "../aggregator";
import CHARACTERS from "../characters";
import { AXIS_KEYS } from "../axes";
import t from "../locales";

export default function DashboardPosts({ posts, setPosts }) {
  const { groupCode } = useParams();
  const [error, setError] = useState(null);
  const [selectedGroupIds, setSelectedGroupIds] = useState(null);

  useEffect(() => {
    if (posts.length === 0) {
      async function loadPosts() {
        const fetchedPosts = await fetchPosts(groupCode);

        if (fetchedPosts === null) {
          setError(t("dashboard.error.other"));
          return;
        }
        if (fetchedPosts.length === 0) {
          setError(t("dashboard.error.empty"));
          return;
        }

        setPosts(fetchedPosts);
      }
      loadPosts();
    }
  }, [groupCode, posts.length, setPosts]);

  const characters = useMemo(
    () =>
      CHARACTERS.map((character) => {
        const groups = posts
          .filter(({ answers }) => answers.finished?.includes(character.key))
          .map(({ public_id, answers }) => ({
            id: public_id,
            name: answers.groupName,
            values: AXIS_KEYS.map((key) => answers[character.key][key] / 100),
          }))
          .toSorted((left, right) =>
            left.name.toLowerCase().localeCompare(right.name.toLowerCase()),
          );

        return {
          ...character,
          name: t(`characters.names.${character.key}`),
          groups,
          average: AXIS_KEYS.map(
            (_, index) =>
              groups.reduce((sum, { values }) => sum + values[index], 0) /
              groups.length,
          ),
        };
      }).filter(({ groups }) => groups.length > 0),
    [posts],
  );

  const groupOptions = posts
    .filter(({ answers }) => answers.finished?.length > 0)
    .map(({ public_id, answers }) => ({
      id: public_id,
      name: answers.groupName,
    }))
    .toSorted((left, right) =>
      left.name.toLowerCase().localeCompare(right.name.toLowerCase()),
    );
  const visibleGroupIds = selectedGroupIds ?? groupOptions.map(({ id }) => id);

  return (
    <article className="card card--dashboard-posts">
      <title>{t("dashboard.posts.page-title")}</title>
      <section className="dashboard-posts">
        <div className="dashboard-posts__header">
          <h1 className="card__title">{t("dashboard.posts.title")}</h1>
          <p className="card__description">
            {t("dashboard.group-code.label")}: {groupCode}
          </p>
        </div>

        {error && <p className="card__description">{error}</p>}
        {posts.length > 0 && characters.length === 0 && (
          <p className="card__description">{t("dashboard.error.empty")}</p>
        )}

        {characters.length > 0 ? (
          <section className="dashboard-posts__panel">
            <div className="dashboard-posts__panel-header">
              <h2
                className="dashboard-posts__panel-title"
                id="dashboard-posts-comparison"
              >
                {t("dashboard.posts.comparison.title")}
              </h2>
            </div>

            <div className="dashboard-posts__chart">
              <SpiderChart
                layers={characters.map(({ name, color, average }) => ({
                  label: name,
                  color,
                  values: average,
                }))}
              />
            </div>

            <div className="dashboard-posts__columns">
              {characters.map(({ key, name, color, average }) => (
                <div key={key} className="dashboard-posts__chart">
                  <h3
                    className="dashboard-posts__chart-title"
                    data-character={key}
                  >
                    {name}
                  </h3>
                  <SpiderChart
                    layers={[{ label: name, color, values: average }]}
                  />
                </div>
              ))}
            </div>
          </section>
        ) : null}
      </section>

      {characters.length > 0 ? (
        <section className="dashboard-posts">
          <div className="dashboard-posts__header">
            <div className="dashboard-posts__heading">
              <h2 className="card__title">
                {t("dashboard.posts.groups.title")}
              </h2>
              <p className="card__description">
                {t("dashboard.posts.groups.description")}
              </p>
            </div>
            <DropdownFilter
              id="dashboard-posts-filter"
              label={
                visibleGroupIds.length === groupOptions.length
                  ? t("dashboard.posts.groups.filter.all")
                  : t("dashboard.posts.groups.filter.some", {
                      count: visibleGroupIds.length,
                    })
              }
              options={groupOptions}
              value={visibleGroupIds}
              onChange={setSelectedGroupIds}
            />
          </div>

          <div className="dashboard-posts__columns">
            {characters.map(({ key, name, color, groups }) => (
              <section
                key={key}
                className="dashboard-posts__panel"
                id={`dashboard-posts-${key}`}
              >
                <h3
                  className="dashboard-posts__chart-title"
                  data-character={key}
                >
                  {name}
                </h3>
                <div className="dashboard-posts__chart dashboard-posts__chart--groups">
                  {groups
                    .filter(({ id }) => visibleGroupIds.includes(id))
                    .map((group) => (
                      <figure key={group.id} className="dashboard-posts__group">
                        <SpiderChart
                          layers={[
                            { label: name, color, values: group.values },
                          ]}
                        />
                        <figcaption className="dashboard-posts__group-name">
                          {group.name}
                        </figcaption>
                      </figure>
                    ))}
                </div>
              </section>
            ))}
          </div>
        </section>
      ) : null}
    </article>
  );
}
